import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  BadgeCheck,
  BriefcaseBusiness,
  Building2,
  Check,
  ClipboardList,
  Globe2,
  Landmark,
  LineChart,
  MonitorSmartphone,
  Presentation,
  Send,
  Sparkles,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { TeacherShowcaseSection } from '@/components/sections/TeacherShowcaseSection';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';
import { countryCodes } from '@/lib/countries';
import { useLang } from '@/contexts/LangContext';
import { useTheme } from '@/contexts/ThemeContext';
import { buildImageUrl } from '@/lib/utils';
import { trackEvent } from '@/lib/tracking';

function pickText(lang, arText, enText) {
  return lang === 'ar' ? arText : enText;
}

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.5, ease: [0.2, 0.8, 0.2, 1] },
  }),
};

const iconMap = {
  BriefcaseBusiness,
  Globe2,
  Users,
  Sparkles,
  Presentation,
  Building2,
  LineChart,
  BadgeCheck,
  ClipboardList,
};

const fallbackContent = {
  hero: {
    eyebrow: 'Corporate Language Training',
    title: 'Corporate Language Training for Companies',
    subtitle: 'Customized English and Arabic language training programs designed to improve communication, productivity, and professional confidence in multicultural workplaces.',
    intro: 'We provide tailored corporate language training solutions for organizations across the UAE. Our programs help teams communicate more effectively in international and multilingual business environments.',
    primaryCtaLabel: 'Request Corporate Training Proposal',
  },
  audience: {
    eyebrow: 'Who Our Training Is For',
    title: 'Built for multicultural, client-facing organizations',
    description: 'Our corporate language programs are designed for organizations operating in international and multicultural environments.',
    deliveryNote: 'Training can be delivered at your office, at our training center, or online.',
    sectors: ['Hospitality', 'Aviation', 'Retail', 'Government', 'Healthcare', 'Banking', 'Corporate services'],
  },
  programs: {
    eyebrow: 'Corporate Programs We Offer',
    title: 'Practical programs for real workplace communication',
    items: [
      {
        title: 'Business English Training',
        description: 'Designed for professionals who need to communicate confidently in international business environments.',
        bullets: ['Business communication', 'Meetings and presentations', 'Email and report writing', 'Negotiation skills', 'Telephone communication', 'Cross-cultural communication', 'Customer service English'],
        footer: 'Best suited for employees working in international teams or client-facing roles.',
        icon: 'BriefcaseBusiness',
      },
      {
        title: 'Arabic for the Workplace',
        description: 'Practical Arabic courses designed for professionals living and working in the UAE.',
        intro: 'Modern spoken Arabic used in real workplace situations.',
        bullets: ['Workplace communication', 'Professional greetings and introductions', 'Customer interaction', 'Telephone communication', 'Service and hospitality Arabic', 'Cultural awareness'],
        footer: 'Ideal for employees working in government relations, hospitality, retail, and public-facing roles.',
        icon: 'Globe2',
      },
    ],
  },
  benefits: {
    eyebrow: 'Corporate Training Benefits',
    title: 'How Corporate Language Training Benefits Your Organization',
    items: [
      { title: 'Improved Communication', description: 'Employees communicate more clearly with international clients and colleagues.', icon: 'Users' },
      { title: 'Stronger Customer Experience', description: 'Better language skills improve service quality and client satisfaction.', icon: 'Sparkles' },
      { title: 'Greater Employee Confidence', description: 'Staff feel more confident participating in meetings and presentations.', icon: 'Presentation' },
      { title: 'Improved Workplace Collaboration', description: 'Teams work more effectively in multicultural environments.', icon: 'Building2' },
      { title: 'Professional Development', description: 'Language training supports employee growth and career development.', icon: 'LineChart' },
    ],
  },
  formats: {
    eyebrow: 'Training Formats',
    title: 'Flexible formats for different business needs',
    items: [
      { title: 'Group Training', description: 'Small groups designed for teams learning together.', bullets: ['4-8 participants', 'Interactive and collaborative', 'Ideal for department training'], icon: 'Users' },
      { title: 'Executive / Private Training', description: 'One-to-one training for managers and executives.', bullets: ['Fully customized curriculum', 'Flexible scheduling', 'Rapid progress'], icon: 'BadgeCheck' },
      { title: 'Intensive Corporate Programs', description: 'Short-term training designed for companies that need fast results.', bullets: ['Presentation skills workshops', 'Customer service English', 'Hospitality English', 'Business writing workshops'], icon: 'ClipboardList' },
    ],
  },
  delivery: {
    eyebrow: 'Course Delivery Options',
    title: 'Delivered where your team learns best',
    description: 'Training can be delivered at your company premises, at our training center, or online through live instructor-led sessions.',
    locationsTitle: 'Training locations',
    locations: ['At your company premises', 'At our training center', 'Online (live instructor-led sessions)'],
    schedulesTitle: 'Flexible schedules',
    schedules: ['Morning', 'Afternoon', 'Evening'],
  },
  process: {
    eyebrow: 'Our Corporate Training Process',
    title: 'A clear process from assessment to measurable progress',
    steps: [
      { step: '01', title: 'Needs Analysis', description: "We assess your team's language level and business communication needs." },
      { step: '02', title: 'Program Design', description: 'We design a customized training plan aligned with your company goals.' },
      { step: '03', title: 'Training Delivery', description: 'Interactive sessions led by experienced instructors.' },
      { step: '04', title: 'Progress Evaluation', description: 'Regular feedback and progress reports for management.' },
    ],
  },
  reasons: {
    eyebrow: 'Why Companies Choose Us',
    title: 'Practical training designed for business outcomes',
    items: ['Experienced corporate trainers', 'Customized programs for each organization', 'Flexible schedules for busy professionals', 'Practical workplace-focused curriculum', 'Progress tracking and reporting'],
  },
  trusted: {
    eyebrow: 'Organizations That Trust Our Training',
    title: 'Trusted by Leading Organizations',
    description: 'Organizations across the UAE trust our language training programs to strengthen communication and professional skills in multicultural workplaces.',
    logos: ['Hospitality Group', 'Aviation Partner', 'Retail Network', 'Healthcare System', 'Banking Partner', 'Public Sector'],
  },
  caseStudy: {
    eyebrow: 'Example Corporate Training Program',
    title: 'Business English Training for Customer Service Teams',
    items: [
      { label: 'Company', value: 'Regional hospitality group in Dubai' },
      { label: 'Objective', value: 'Improve communication between frontline staff and international guests.' },
      { label: 'Training format', value: 'Group training (6 participants)' },
      { label: 'Program duration', value: '8 weeks' },
    ],
    focusTitle: 'Focus areas',
    focusAreas: ['Guest communication', 'Service interaction', 'Complaint handling', 'Professional email communication', 'Cross-cultural communication'],
    resultTitle: 'Result',
    result: 'Staff improved confidence in English communication and customer interaction, leading to improved guest satisfaction.',
  },
  proposal: {
    eyebrow: 'Request a Corporate Training Proposal',
    title: 'Tell Us About Your Training Needs',
    description: 'Share a few details about your organization and our team will prepare a customized training proposal.',
    submitLabel: 'Request Proposal',
  },
  finalCta: {
    eyebrow: 'Final CTA',
    title: 'Ready to Train Your Team?',
    description: "Contact us today to discuss your organization's language training needs and receive a customized corporate training proposal.",
    primaryLabel: 'Request Corporate Training Proposal',
    secondaryLabel: 'Contact Us',
  },
};

const fallbackContentAr = {
  hero: {
    eyebrow: 'Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã˜Â§Ã™â€žÃ™â€žÃ˜ÂºÃ™Ë†Ã™Å  Ã™â€žÃ™â€žÃ˜Â´Ã˜Â±Ã™Æ’Ã˜Â§Ã˜Âª',
    title: 'Ã˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã™â€žÃ˜ÂºÃ™Ë†Ã™Å  Ã™â€¦Ã˜Â¤Ã˜Â³Ã˜Â³Ã™Å  Ã™â€žÃ™â€žÃ˜Â´Ã˜Â±Ã™Æ’Ã˜Â§Ã˜Âª',
    subtitle: 'Ã˜Â¨Ã˜Â±Ã˜Â§Ã™â€¦Ã˜Â¬ Ã™â€¦Ã˜Â®Ã˜ÂµÃ˜ÂµÃ˜Â© Ã™ÂÃ™Å  Ã˜Â§Ã™â€žÃ˜Â¥Ã™â€ Ã˜Â¬Ã™â€žÃ™Å Ã˜Â²Ã™Å Ã˜Â© Ã™Ë†Ã˜Â§Ã™â€žÃ˜Â¹Ã˜Â±Ã˜Â¨Ã™Å Ã˜Â© Ã™â€žÃ˜ÂªÃ˜Â­Ã˜Â³Ã™Å Ã™â€  Ã˜Â§Ã™â€žÃ˜ÂªÃ™Ë†Ã˜Â§Ã˜ÂµÃ™â€ž Ã™Ë†Ã˜Â§Ã™â€žÃ˜Â¥Ã™â€ Ã˜ÂªÃ˜Â§Ã˜Â¬Ã™Å Ã˜Â© Ã™Ë†Ã˜Â§Ã™â€žÃ˜Â«Ã™â€šÃ˜Â© Ã˜Â§Ã™â€žÃ™â€¦Ã™â€¡Ã™â€ Ã™Å Ã˜Â© Ã™ÂÃ™Å  Ã˜Â¨Ã™Å Ã˜Â¦Ã˜Â§Ã˜Âª Ã˜Â§Ã™â€žÃ˜Â¹Ã™â€¦Ã™â€ž Ã™â€¦Ã˜ÂªÃ˜Â¹Ã˜Â¯Ã˜Â¯Ã˜Â© Ã˜Â§Ã™â€žÃ˜Â«Ã™â€šÃ˜Â§Ã™ÂÃ˜Â§Ã˜Âª.',
    intro: 'Ã™â€ Ã™â€šÃ˜Â¯Ã™â€¦ Ã˜Â­Ã™â€žÃ™Ë†Ã™â€ž Ã˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã™â€žÃ˜ÂºÃ™Ë†Ã™Å  Ã™â€¦Ã˜Â¤Ã˜Â³Ã˜Â³Ã™Å  Ã™â€¦Ã˜ÂµÃ™â€¦Ã™â€¦Ã˜Â© Ã˜Â®Ã˜ÂµÃ™Å Ã˜ÂµÃ™â€¹Ã˜Â§ Ã™â€žÃ™â€žÃ™â€¦Ã™â€ Ã˜Â¸Ã™â€¦Ã˜Â§Ã˜Âª Ã™ÂÃ™Å  Ã˜Â¬Ã™â€¦Ã™Å Ã˜Â¹ Ã˜Â£Ã™â€ Ã˜Â­Ã˜Â§Ã˜Â¡ Ã˜Â§Ã™â€žÃ˜Â¥Ã™â€¦Ã˜Â§Ã˜Â±Ã˜Â§Ã˜Âª. Ã˜ÂªÃ˜Â³Ã˜Â§Ã˜Â¹Ã˜Â¯ Ã˜Â¨Ã˜Â±Ã˜Â§Ã™â€¦Ã˜Â¬Ã™â€ Ã˜Â§ Ã˜Â§Ã™â€žÃ™ÂÃ˜Â±Ã™â€š Ã˜Â¹Ã™â€žÃ™â€° Ã˜Â§Ã™â€žÃ˜ÂªÃ™Ë†Ã˜Â§Ã˜ÂµÃ™â€ž Ã˜Â¨Ã™ÂÃ˜Â§Ã˜Â¹Ã™â€žÃ™Å Ã˜Â© Ã˜Â£Ã™Æ’Ã˜Â¨Ã˜Â± Ã™ÂÃ™Å  Ã˜Â¨Ã™Å Ã˜Â¦Ã˜Â§Ã˜Âª Ã˜Â¹Ã™â€¦Ã™â€ž Ã˜Â¯Ã™Ë†Ã™â€žÃ™Å Ã˜Â© Ã™Ë†Ã™â€¦Ã˜ÂªÃ˜Â¹Ã˜Â¯Ã˜Â¯Ã˜Â© Ã˜Â§Ã™â€žÃ™â€žÃ˜ÂºÃ˜Â§Ã˜Âª.',
    primaryCtaLabel: 'Ã˜Â§Ã˜Â·Ã™â€žÃ˜Â¨ Ã˜Â¹Ã˜Â±Ã˜Â¶ Ã˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã™â€¦Ã˜Â¤Ã˜Â³Ã˜Â³Ã™Å ',
  },
  audience: {
    eyebrow: 'Ã™â€žÃ™â€¦Ã™â€  Ã˜ÂµÃ™ÂÃ™â€¦Ã™â€¦ Ã™â€¡Ã˜Â°Ã˜Â§ Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨',
    title: 'Ã™â€¦Ã˜ÂµÃ™â€¦Ã™â€¦ Ã™â€žÃ™â€žÃ™â€¦Ã˜Â¤Ã˜Â³Ã˜Â³Ã˜Â§Ã˜Âª Ã™â€¦Ã˜ÂªÃ˜Â¹Ã˜Â¯Ã˜Â¯Ã˜Â© Ã˜Â§Ã™â€žÃ˜Â«Ã™â€šÃ˜Â§Ã™ÂÃ˜Â§Ã˜Âª Ã™Ë†Ã˜Â§Ã™â€žÃ™â€¦Ã˜ÂªÃ˜Â¹Ã˜Â§Ã™â€¦Ã™â€žÃ˜Â© Ã™â€¦Ã˜Â¹ Ã˜Â§Ã™â€žÃ˜Â¹Ã™â€¦Ã™â€žÃ˜Â§Ã˜Â¡',
    description: 'Ã˜Â¨Ã˜Â±Ã˜Â§Ã™â€¦Ã˜Â¬Ã™â€ Ã˜Â§ Ã˜Â§Ã™â€žÃ™â€žÃ˜ÂºÃ™Ë†Ã™Å Ã˜Â© Ã˜Â§Ã™â€žÃ™â€¦Ã˜Â¤Ã˜Â³Ã˜Â³Ã™Å Ã˜Â© Ã™â€¦Ã˜ÂµÃ™â€¦Ã™â€¦Ã˜Â© Ã™â€žÃ™â€žÃ™â€¦Ã™â€ Ã˜Â¸Ã™â€¦Ã˜Â§Ã˜Âª Ã˜Â§Ã™â€žÃ˜ÂªÃ™Å  Ã˜ÂªÃ˜Â¹Ã™â€¦Ã™â€ž Ã™ÂÃ™Å  Ã˜Â¨Ã™Å Ã˜Â¦Ã˜Â§Ã˜Âª Ã˜Â¯Ã™Ë†Ã™â€žÃ™Å Ã˜Â© Ã™Ë†Ã™â€¦Ã˜ÂªÃ˜Â¹Ã˜Â¯Ã˜Â¯Ã˜Â© Ã˜Â§Ã™â€žÃ˜Â«Ã™â€šÃ˜Â§Ã™ÂÃ˜Â§Ã˜Âª.',
    deliveryNote: 'Ã™Å Ã™â€¦Ã™Æ’Ã™â€  Ã˜ÂªÃ™â€šÃ˜Â¯Ã™Å Ã™â€¦ Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã™ÂÃ™Å  Ã™â€¦Ã™â€šÃ˜Â± Ã˜Â´Ã˜Â±Ã™Æ’Ã˜ÂªÃ™Æ’Ã™â€¦ Ã˜Â£Ã™Ë† Ã™ÂÃ™Å  Ã™â€¦Ã˜Â±Ã™Æ’Ã˜Â²Ã™â€ Ã˜Â§ Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨Ã™Å  Ã˜Â£Ã™Ë† Ã˜Â¹Ã˜Â¨Ã˜Â± Ã˜Â§Ã™â€žÃ˜Â¥Ã™â€ Ã˜ÂªÃ˜Â±Ã™â€ Ã˜Âª.',
    sectors: ['Ã˜Â§Ã™â€žÃ˜Â¶Ã™Å Ã˜Â§Ã™ÂÃ˜Â©', 'Ã˜Â§Ã™â€žÃ˜Â·Ã™Å Ã˜Â±Ã˜Â§Ã™â€ ', 'Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¬Ã˜Â²Ã˜Â¦Ã˜Â©', 'Ã˜Â§Ã™â€žÃ™â€šÃ˜Â·Ã˜Â§Ã˜Â¹ Ã˜Â§Ã™â€žÃ˜Â­Ã™Æ’Ã™Ë†Ã™â€¦Ã™Å ', 'Ã˜Â§Ã™â€žÃ˜Â±Ã˜Â¹Ã˜Â§Ã™Å Ã˜Â© Ã˜Â§Ã™â€žÃ˜ÂµÃ˜Â­Ã™Å Ã˜Â©', 'Ã˜Â§Ã™â€žÃ˜Â®Ã˜Â¯Ã™â€¦Ã˜Â§Ã˜Âª Ã˜Â§Ã™â€žÃ™â€¦Ã˜ÂµÃ˜Â±Ã™ÂÃ™Å Ã˜Â©', 'Ã˜Â§Ã™â€žÃ˜Â®Ã˜Â¯Ã™â€¦Ã˜Â§Ã˜Âª Ã˜Â§Ã™â€žÃ™â€¦Ã˜Â¤Ã˜Â³Ã˜Â³Ã™Å Ã˜Â©'],
  },
  programs: {
    eyebrow: 'Ã˜Â¨Ã˜Â±Ã˜Â§Ã™â€¦Ã˜Â¬Ã™â€ Ã˜Â§ Ã˜Â§Ã™â€žÃ™â€¦Ã˜Â¤Ã˜Â³Ã˜Â³Ã™Å Ã˜Â©',
    title: 'Ã˜Â¨Ã˜Â±Ã˜Â§Ã™â€¦Ã˜Â¬ Ã˜Â¹Ã™â€¦Ã™â€žÃ™Å Ã˜Â© Ã™â€žÃ˜ÂªÃ™Ë†Ã˜Â§Ã˜ÂµÃ™â€ž Ã˜Â­Ã™â€šÃ™Å Ã™â€šÃ™Å  Ã™ÂÃ™Å  Ã˜Â¨Ã™Å Ã˜Â¦Ã˜Â© Ã˜Â§Ã™â€žÃ˜Â¹Ã™â€¦Ã™â€ž',
    items: [
      {
        title: 'Ã˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã˜Â§Ã™â€žÃ˜Â¥Ã™â€ Ã˜Â¬Ã™â€žÃ™Å Ã˜Â²Ã™Å Ã˜Â© Ã™â€žÃ™â€žÃ˜Â£Ã˜Â¹Ã™â€¦Ã˜Â§Ã™â€ž',
        description: 'Ã™â€¦Ã˜ÂµÃ™â€¦Ã™â€¦ Ã™â€žÃ™â€žÃ™â€¦Ã™â€¡Ã™â€ Ã™Å Ã™Å Ã™â€  Ã˜Â§Ã™â€žÃ˜Â°Ã™Å Ã™â€  Ã™Å Ã˜Â­Ã˜ÂªÃ˜Â§Ã˜Â¬Ã™Ë†Ã™â€  Ã˜Â¥Ã™â€žÃ™â€° Ã˜Â§Ã™â€žÃ˜ÂªÃ™Ë†Ã˜Â§Ã˜ÂµÃ™â€ž Ã˜Â¨Ã˜Â«Ã™â€šÃ˜Â© Ã™ÂÃ™Å  Ã˜Â¨Ã™Å Ã˜Â¦Ã˜Â§Ã˜Âª Ã˜Â§Ã™â€žÃ˜Â£Ã˜Â¹Ã™â€¦Ã˜Â§Ã™â€ž Ã˜Â§Ã™â€žÃ˜Â¯Ã™Ë†Ã™â€žÃ™Å Ã˜Â©.',
        bullets: ['Ã˜Â§Ã™â€žÃ˜ÂªÃ™Ë†Ã˜Â§Ã˜ÂµÃ™â€ž Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¬Ã˜Â§Ã˜Â±Ã™Å ', 'Ã˜Â§Ã™â€žÃ˜Â§Ã˜Â¬Ã˜ÂªÃ™â€¦Ã˜Â§Ã˜Â¹Ã˜Â§Ã˜Âª Ã™Ë†Ã˜Â§Ã™â€žÃ˜Â¹Ã˜Â±Ã™Ë†Ã˜Â¶ Ã˜Â§Ã™â€žÃ˜ÂªÃ™â€šÃ˜Â¯Ã™Å Ã™â€¦Ã™Å Ã˜Â©', 'Ã™Æ’Ã˜ÂªÃ˜Â§Ã˜Â¨Ã˜Â© Ã˜Â§Ã™â€žÃ˜Â¨Ã˜Â±Ã™Å Ã˜Â¯ Ã˜Â§Ã™â€žÃ˜Â¥Ã™â€žÃ™Æ’Ã˜ÂªÃ˜Â±Ã™Ë†Ã™â€ Ã™Å  Ã™Ë†Ã˜Â§Ã™â€žÃ˜ÂªÃ™â€šÃ˜Â§Ã˜Â±Ã™Å Ã˜Â±', 'Ã™â€¦Ã™â€¡Ã˜Â§Ã˜Â±Ã˜Â§Ã˜Âª Ã˜Â§Ã™â€žÃ˜ÂªÃ™ÂÃ˜Â§Ã™Ë†Ã˜Â¶', 'Ã˜Â§Ã™â€žÃ˜ÂªÃ™Ë†Ã˜Â§Ã˜ÂµÃ™â€ž Ã˜Â§Ã™â€žÃ™â€¡Ã˜Â§Ã˜ÂªÃ™ÂÃ™Å ', 'Ã˜Â§Ã™â€žÃ˜ÂªÃ™Ë†Ã˜Â§Ã˜ÂµÃ™â€ž Ã˜Â¨Ã™Å Ã™â€  Ã˜Â§Ã™â€žÃ˜Â«Ã™â€šÃ˜Â§Ã™ÂÃ˜Â§Ã˜Âª', 'Ã˜Â§Ã™â€žÃ˜Â¥Ã™â€ Ã˜Â¬Ã™â€žÃ™Å Ã˜Â²Ã™Å Ã˜Â© Ã™â€žÃ˜Â®Ã˜Â¯Ã™â€¦Ã˜Â© Ã˜Â§Ã™â€žÃ˜Â¹Ã™â€¦Ã™â€žÃ˜Â§Ã˜Â¡'],
        footer: 'Ã™â€¦Ã™â€ Ã˜Â§Ã˜Â³Ã˜Â¨ Ã™â€žÃ™â€žÃ™â€¦Ã™Ë†Ã˜Â¸Ã™ÂÃ™Å Ã™â€  Ã˜Â§Ã™â€žÃ˜Â°Ã™Å Ã™â€  Ã™Å Ã˜Â¹Ã™â€¦Ã™â€žÃ™Ë†Ã™â€  Ã˜Â¶Ã™â€¦Ã™â€  Ã™ÂÃ˜Â±Ã™â€š Ã˜Â¯Ã™Ë†Ã™â€žÃ™Å Ã˜Â© Ã˜Â£Ã™Ë† Ã™ÂÃ™Å  Ã™Ë†Ã˜Â¸Ã˜Â§Ã˜Â¦Ã™Â Ã˜ÂªÃ˜ÂªÃ˜Â¹Ã˜Â§Ã™â€¦Ã™â€ž Ã™â€¦Ã˜Â¨Ã˜Â§Ã˜Â´Ã˜Â±Ã˜Â© Ã™â€¦Ã˜Â¹ Ã˜Â§Ã™â€žÃ˜Â¹Ã™â€¦Ã™â€žÃ˜Â§Ã˜Â¡.',
        icon: 'BriefcaseBusiness',
      },
      {
        title: 'Ã˜Â§Ã™â€žÃ˜Â¹Ã˜Â±Ã˜Â¨Ã™Å Ã˜Â© Ã™â€žÃ˜Â¨Ã™Å Ã˜Â¦Ã˜Â© Ã˜Â§Ã™â€žÃ˜Â¹Ã™â€¦Ã™â€ž',
        description: 'Ã˜Â¯Ã™Ë†Ã˜Â±Ã˜Â§Ã˜Âª Ã˜Â¹Ã˜Â±Ã˜Â¨Ã™Å Ã˜Â© Ã˜Â¹Ã™â€¦Ã™â€žÃ™Å Ã˜Â© Ã™â€¦Ã˜ÂµÃ™â€¦Ã™â€¦Ã˜Â© Ã™â€žÃ™â€žÃ™â€¦Ã™â€¡Ã™â€ Ã™Å Ã™Å Ã™â€  Ã˜Â§Ã™â€žÃ˜Â°Ã™Å Ã™â€  Ã™Å Ã˜Â¹Ã™Å Ã˜Â´Ã™Ë†Ã™â€  Ã™Ë†Ã™Å Ã˜Â¹Ã™â€¦Ã™â€žÃ™Ë†Ã™â€  Ã™ÂÃ™Å  Ã˜Â§Ã™â€žÃ˜Â¥Ã™â€¦Ã˜Â§Ã˜Â±Ã˜Â§Ã˜Âª.',
        intro: 'Ã˜Â¹Ã˜Â±Ã˜Â¨Ã™Å Ã˜Â© Ã˜Â­Ã˜Â¯Ã™Å Ã˜Â«Ã˜Â© Ã™â€¦Ã˜Â­Ã™Æ’Ã™Å Ã˜Â© Ã˜ÂªÃ™ÂÃ˜Â³Ã˜ÂªÃ˜Â®Ã˜Â¯Ã™â€¦ Ã™ÂÃ™Å  Ã™â€¦Ã™Ë†Ã˜Â§Ã™â€šÃ™Â Ã˜Â§Ã™â€žÃ˜Â¹Ã™â€¦Ã™â€ž Ã˜Â§Ã™â€žÃ™Å Ã™Ë†Ã™â€¦Ã™Å Ã˜Â©.',
        bullets: ['Ã˜Â§Ã™â€žÃ˜ÂªÃ™Ë†Ã˜Â§Ã˜ÂµÃ™â€ž Ã™ÂÃ™Å  Ã™â€¦Ã™Æ’Ã˜Â§Ã™â€  Ã˜Â§Ã™â€žÃ˜Â¹Ã™â€¦Ã™â€ž', 'Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â­Ã™Å Ã˜Â§Ã˜Âª Ã™Ë†Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¹Ã˜Â±Ã™Å Ã™ÂÃ˜Â§Ã˜Âª Ã˜Â§Ã™â€žÃ™â€¦Ã™â€¡Ã™â€ Ã™Å Ã˜Â©', 'Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¹Ã˜Â§Ã™â€¦Ã™â€ž Ã™â€¦Ã˜Â¹ Ã˜Â§Ã™â€žÃ˜Â¹Ã™â€¦Ã™â€žÃ˜Â§Ã˜Â¡', 'Ã˜Â§Ã™â€žÃ˜ÂªÃ™Ë†Ã˜Â§Ã˜ÂµÃ™â€ž Ã˜Â§Ã™â€žÃ™â€¡Ã˜Â§Ã˜ÂªÃ™ÂÃ™Å ', 'Ã˜Â§Ã™â€žÃ˜Â¹Ã˜Â±Ã˜Â¨Ã™Å Ã˜Â© Ã™â€žÃ™â€žÃ˜Â®Ã˜Â¯Ã™â€¦Ã˜Â© Ã™Ë†Ã˜Â§Ã™â€žÃ˜Â¶Ã™Å Ã˜Â§Ã™ÂÃ˜Â©', 'Ã˜Â§Ã™â€žÃ™Ë†Ã˜Â¹Ã™Å  Ã˜Â§Ã™â€žÃ˜Â«Ã™â€šÃ˜Â§Ã™ÂÃ™Å '],
        footer: 'Ã™â€¦Ã˜Â«Ã˜Â§Ã™â€žÃ™Å  Ã™â€žÃ™â€žÃ™â€¦Ã™Ë†Ã˜Â¸Ã™ÂÃ™Å Ã™â€  Ã˜Â§Ã™â€žÃ˜Â¹Ã˜Â§Ã™â€¦Ã™â€žÃ™Å Ã™â€  Ã™ÂÃ™Å  Ã˜Â§Ã™â€žÃ˜Â¹Ã™â€žÃ˜Â§Ã™â€šÃ˜Â§Ã˜Âª Ã˜Â§Ã™â€žÃ˜Â­Ã™Æ’Ã™Ë†Ã™â€¦Ã™Å Ã˜Â© Ã™Ë†Ã˜Â§Ã™â€žÃ˜Â¶Ã™Å Ã˜Â§Ã™ÂÃ˜Â© Ã™Ë†Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¬Ã˜Â²Ã˜Â¦Ã˜Â© Ã™Ë†Ã˜Â§Ã™â€žÃ˜Â£Ã˜Â¯Ã™Ë†Ã˜Â§Ã˜Â± Ã˜Â§Ã™â€žÃ˜ÂªÃ™Å  Ã˜ÂªÃ˜ÂªÃ˜Â·Ã™â€žÃ˜Â¨ Ã˜ÂªÃ˜Â¹Ã˜Â§Ã™â€¦Ã™â€žÃ™â€¹Ã˜Â§ Ã™â€¦Ã˜Â¨Ã˜Â§Ã˜Â´Ã˜Â±Ã™â€¹Ã˜Â§ Ã™â€¦Ã˜Â¹ Ã˜Â§Ã™â€žÃ˜Â¬Ã™â€¦Ã™â€¡Ã™Ë†Ã˜Â±.',
        icon: 'Globe2',
      },
    ],
  },
  benefits: {
    eyebrow: 'Ã™ÂÃ™Ë†Ã˜Â§Ã˜Â¦Ã˜Â¯ Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã˜Â§Ã™â€žÃ™â€¦Ã˜Â¤Ã˜Â³Ã˜Â³Ã™Å ',
    title: 'Ã™Æ’Ã™Å Ã™Â Ã™Å Ã™ÂÃ™Å Ã˜Â¯ Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã˜Â§Ã™â€žÃ™â€žÃ˜ÂºÃ™Ë†Ã™Å  Ã˜Â§Ã™â€žÃ™â€¦Ã˜Â¤Ã˜Â³Ã˜Â³Ã™Å  Ã™â€¦Ã™â€ Ã˜Â¸Ã™â€¦Ã˜ÂªÃ™Æ’Ã™â€¦',
    items: [
      { title: 'Ã˜ÂªÃ˜Â­Ã˜Â³Ã™Å Ã™â€  Ã˜Â§Ã™â€žÃ˜ÂªÃ™Ë†Ã˜Â§Ã˜ÂµÃ™â€ž', description: 'Ã™Å Ã˜ÂªÃ™Ë†Ã˜Â§Ã˜ÂµÃ™â€ž Ã˜Â§Ã™â€žÃ™â€¦Ã™Ë†Ã˜Â¸Ã™ÂÃ™Ë†Ã™â€  Ã˜Â¨Ã™Ë†Ã˜Â¶Ã™Ë†Ã˜Â­ Ã˜Â£Ã™Æ’Ã˜Â¨Ã˜Â± Ã™â€¦Ã˜Â¹ Ã˜Â§Ã™â€žÃ˜Â¹Ã™â€¦Ã™â€žÃ˜Â§Ã˜Â¡ Ã™Ë†Ã˜Â§Ã™â€žÃ˜Â²Ã™â€¦Ã™â€žÃ˜Â§Ã˜Â¡ Ã˜Â§Ã™â€žÃ˜Â¯Ã™Ë†Ã™â€žÃ™Å Ã™Å Ã™â€ .', icon: 'Users' },
      { title: 'Ã˜ÂªÃ˜Â¬Ã˜Â±Ã˜Â¨Ã˜Â© Ã˜Â¹Ã™â€¦Ã™â€žÃ˜Â§Ã˜Â¡ Ã˜Â£Ã™â€šÃ™Ë†Ã™â€°', description: 'Ã˜ÂªÃ˜Â­Ã˜Â³Ã™â€  Ã˜Â§Ã™â€žÃ™â€¦Ã™â€¡Ã˜Â§Ã˜Â±Ã˜Â§Ã˜Âª Ã˜Â§Ã™â€žÃ™â€žÃ˜ÂºÃ™Ë†Ã™Å Ã˜Â© Ã˜Â¬Ã™Ë†Ã˜Â¯Ã˜Â© Ã˜Â§Ã™â€žÃ˜Â®Ã˜Â¯Ã™â€¦Ã˜Â© Ã™Ë†Ã˜Â±Ã˜Â¶Ã˜Â§ Ã˜Â§Ã™â€žÃ˜Â¹Ã™â€¦Ã™â€žÃ˜Â§Ã˜Â¡.', icon: 'Sparkles' },
      { title: 'Ã˜Â«Ã™â€šÃ˜Â© Ã˜Â£Ã™Æ’Ã˜Â¨Ã˜Â± Ã™â€žÃ™â€žÃ™â€¦Ã™Ë†Ã˜Â¸Ã™ÂÃ™Å Ã™â€ ', description: 'Ã™Å Ã˜Â´Ã˜Â¹Ã˜Â± Ã˜Â§Ã™â€žÃ™â€¦Ã™Ë†Ã˜Â¸Ã™ÂÃ™Ë†Ã™â€  Ã˜Â¨Ã˜Â«Ã™â€šÃ˜Â© Ã˜Â£Ã™Æ’Ã˜Â¨Ã˜Â± Ã˜Â¹Ã™â€ Ã˜Â¯ Ã˜Â§Ã™â€žÃ™â€¦Ã˜Â´Ã˜Â§Ã˜Â±Ã™Æ’Ã˜Â© Ã™ÂÃ™Å  Ã˜Â§Ã™â€žÃ˜Â§Ã˜Â¬Ã˜ÂªÃ™â€¦Ã˜Â§Ã˜Â¹Ã˜Â§Ã˜Âª Ã™Ë†Ã˜Â§Ã™â€žÃ˜Â¹Ã˜Â±Ã™Ë†Ã˜Â¶ Ã˜Â§Ã™â€žÃ˜ÂªÃ™â€šÃ˜Â¯Ã™Å Ã™â€¦Ã™Å Ã˜Â©.', icon: 'Presentation' },
      { title: 'Ã˜ÂªÃ˜Â¹Ã˜Â§Ã™Ë†Ã™â€  Ã˜Â£Ã™ÂÃ˜Â¶Ã™â€ž Ã™ÂÃ™Å  Ã˜Â§Ã™â€žÃ˜Â¹Ã™â€¦Ã™â€ž', description: 'Ã˜ÂªÃ˜Â¹Ã™â€¦Ã™â€ž Ã˜Â§Ã™â€žÃ™ÂÃ˜Â±Ã™â€š Ã˜Â¨Ã™ÂÃ˜Â§Ã˜Â¹Ã™â€žÃ™Å Ã˜Â© Ã˜Â£Ã™Æ’Ã˜Â¨Ã˜Â± Ã™ÂÃ™Å  Ã˜Â§Ã™â€žÃ˜Â¨Ã™Å Ã˜Â¦Ã˜Â§Ã˜Âª Ã™â€¦Ã˜ÂªÃ˜Â¹Ã˜Â¯Ã˜Â¯Ã˜Â© Ã˜Â§Ã™â€žÃ˜Â«Ã™â€šÃ˜Â§Ã™ÂÃ˜Â§Ã˜Âª.', icon: 'Building2' },
      { title: 'Ã˜ÂªÃ˜Â·Ã™Ë†Ã™Å Ã˜Â± Ã™â€¦Ã™â€¡Ã™â€ Ã™Å ', description: 'Ã™Å Ã˜Â¯Ã˜Â¹Ã™â€¦ Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã˜Â§Ã™â€žÃ™â€žÃ˜ÂºÃ™Ë†Ã™Å  Ã™â€ Ã™â€¦Ã™Ë† Ã˜Â§Ã™â€žÃ™â€¦Ã™Ë†Ã˜Â¸Ã™ÂÃ™Å Ã™â€  Ã™Ë†Ã˜ÂªÃ˜Â·Ã™Ë†Ã˜Â±Ã™â€¡Ã™â€¦ Ã˜Â§Ã™â€žÃ™â€¦Ã™â€¡Ã™â€ Ã™Å .', icon: 'LineChart' },
    ],
  },
  formats: {
    eyebrow: 'Ã˜Â£Ã™â€ Ã™â€¦Ã˜Â§Ã˜Â· Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨',
    title: 'Ã˜Â®Ã™Å Ã˜Â§Ã˜Â±Ã˜Â§Ã˜Âª Ã™â€¦Ã˜Â±Ã™â€ Ã˜Â© Ã™â€žÃ˜Â§Ã˜Â­Ã˜ÂªÃ™Å Ã˜Â§Ã˜Â¬Ã˜Â§Ã˜Âª Ã˜Â£Ã˜Â¹Ã™â€¦Ã˜Â§Ã™â€ž Ã™â€¦Ã˜Â®Ã˜ÂªÃ™â€žÃ™ÂÃ˜Â©',
    items: [
      { title: 'Ã˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã˜Â¬Ã™â€¦Ã˜Â§Ã˜Â¹Ã™Å ', description: 'Ã™â€¦Ã˜Â¬Ã™â€¦Ã™Ë†Ã˜Â¹Ã˜Â§Ã˜Âª Ã˜ÂµÃ˜ÂºÃ™Å Ã˜Â±Ã˜Â© Ã™â€¦Ã˜ÂµÃ™â€¦Ã™â€¦Ã˜Â© Ã™â€žÃ™â€žÃ™ÂÃ˜Â±Ã™â€š Ã˜Â§Ã™â€žÃ˜ÂªÃ™Å  Ã˜ÂªÃ˜ÂªÃ˜Â¹Ã™â€žÃ™â€¦ Ã™â€¦Ã˜Â¹Ã™â€¹Ã˜Â§.', bullets: ['4-8 Ã™â€¦Ã˜Â´Ã˜Â§Ã˜Â±Ã™Æ’Ã™Å Ã™â€ ', 'Ã˜ÂªÃ™ÂÃ˜Â§Ã˜Â¹Ã™â€žÃ™Å  Ã™Ë†Ã˜ÂªÃ˜Â¹Ã˜Â§Ã™Ë†Ã™â€ Ã™Å ', 'Ã™â€¦Ã˜Â«Ã˜Â§Ã™â€žÃ™Å  Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã˜Â§Ã™â€žÃ˜Â£Ã™â€šÃ˜Â³Ã˜Â§Ã™â€¦'], icon: 'Users' },
      { title: 'Ã˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã˜ÂªÃ™â€ Ã™ÂÃ™Å Ã˜Â°Ã™Å  / Ã˜Â®Ã˜Â§Ã˜Âµ', description: 'Ã˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã™ÂÃ˜Â±Ã˜Â¯Ã™Å  Ã™â€žÃ™â€žÃ™â€¦Ã˜Â¯Ã™Å Ã˜Â±Ã™Å Ã™â€  Ã™Ë†Ã˜Â§Ã™â€žÃ˜ÂªÃ™â€ Ã™ÂÃ™Å Ã˜Â°Ã™Å Ã™Å Ã™â€ .', bullets: ['Ã™â€¦Ã™â€ Ã™â€¡Ã˜Â¬ Ã™â€¦Ã˜Â®Ã˜ÂµÃ˜Âµ Ã˜Â¨Ã˜Â§Ã™â€žÃ™Æ’Ã˜Â§Ã™â€¦Ã™â€ž', 'Ã˜Â¬Ã˜Â¯Ã˜Â§Ã™Ë†Ã™â€ž Ã™â€¦Ã˜Â±Ã™â€ Ã˜Â©', 'Ã˜ÂªÃ™â€šÃ˜Â¯Ã™â€¦ Ã˜Â³Ã˜Â±Ã™Å Ã˜Â¹'], icon: 'BadgeCheck' },
      { title: 'Ã˜Â¨Ã˜Â±Ã˜Â§Ã™â€¦Ã˜Â¬ Ã™â€¦Ã˜Â¤Ã˜Â³Ã˜Â³Ã™Å Ã˜Â© Ã™â€¦Ã™Æ’Ã˜Â«Ã™ÂÃ˜Â©', description: 'Ã˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã™â€šÃ˜ÂµÃ™Å Ã˜Â± Ã˜Â§Ã™â€žÃ™â€¦Ã˜Â¯Ã™â€° Ã™â€žÃ™â€žÃ˜Â´Ã˜Â±Ã™Æ’Ã˜Â§Ã˜Âª Ã˜Â§Ã™â€žÃ˜ÂªÃ™Å  Ã˜ÂªÃ˜Â­Ã˜ÂªÃ˜Â§Ã˜Â¬ Ã˜Â¥Ã™â€žÃ™â€° Ã™â€ Ã˜ÂªÃ˜Â§Ã˜Â¦Ã˜Â¬ Ã˜Â³Ã˜Â±Ã™Å Ã˜Â¹Ã˜Â©.', bullets: ['Ã™Ë†Ã˜Â±Ã˜Â´ Ã™â€¦Ã™â€¡Ã˜Â§Ã˜Â±Ã˜Â§Ã˜Âª Ã˜Â§Ã™â€žÃ˜Â¹Ã˜Â±Ã˜Â¶', 'Ã˜Â§Ã™â€žÃ˜Â¥Ã™â€ Ã˜Â¬Ã™â€žÃ™Å Ã˜Â²Ã™Å Ã˜Â© Ã™â€žÃ˜Â®Ã˜Â¯Ã™â€¦Ã˜Â© Ã˜Â§Ã™â€žÃ˜Â¹Ã™â€¦Ã™â€žÃ˜Â§Ã˜Â¡', 'Ã˜Â§Ã™â€žÃ˜Â¥Ã™â€ Ã˜Â¬Ã™â€žÃ™Å Ã˜Â²Ã™Å Ã˜Â© Ã™â€žÃ™â€žÃ˜Â¶Ã™Å Ã˜Â§Ã™ÂÃ˜Â©', 'Ã™Ë†Ã˜Â±Ã˜Â´ Ã˜Â§Ã™â€žÃ™Æ’Ã˜ÂªÃ˜Â§Ã˜Â¨Ã˜Â© Ã˜Â§Ã™â€žÃ™â€¦Ã™â€¡Ã™â€ Ã™Å Ã˜Â©'], icon: 'ClipboardList' },
    ],
  },
  delivery: {
    eyebrow: 'Ã˜Â®Ã™Å Ã˜Â§Ã˜Â±Ã˜Â§Ã˜Âª Ã˜ÂªÃ™â€šÃ˜Â¯Ã™Å Ã™â€¦ Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨',
    title: 'Ã™Å Ã™ÂÃ™â€šÃ˜Â¯Ã™â€˜Ã™Å½Ã™â€¦ Ã˜Â­Ã™Å Ã˜Â« Ã™Å Ã˜ÂªÃ˜Â¹Ã™â€žÃ™â€¦ Ã™ÂÃ˜Â±Ã™Å Ã™â€šÃ™Æ’ Ã˜Â¨Ã˜Â£Ã™ÂÃ˜Â¶Ã™â€ž Ã˜Â´Ã™Æ’Ã™â€ž',
    description: 'Ã™Å Ã™â€¦Ã™Æ’Ã™â€  Ã˜ÂªÃ™â€šÃ˜Â¯Ã™Å Ã™â€¦ Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã™ÂÃ™Å  Ã™â€¦Ã™â€šÃ˜Â± Ã˜Â´Ã˜Â±Ã™Æ’Ã˜ÂªÃ™Æ’Ã™â€¦ Ã˜Â£Ã™Ë† Ã™ÂÃ™Å  Ã™â€¦Ã˜Â±Ã™Æ’Ã˜Â²Ã™â€ Ã˜Â§ Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨Ã™Å  Ã˜Â£Ã™Ë† Ã˜Â¹Ã˜Â¨Ã˜Â± Ã˜Â¬Ã™â€žÃ˜Â³Ã˜Â§Ã˜Âª Ã™â€¦Ã˜Â¨Ã˜Â§Ã˜Â´Ã˜Â±Ã˜Â© Ã™Å Ã™â€šÃ™Ë†Ã˜Â¯Ã™â€¡Ã˜Â§ Ã˜Â§Ã™â€žÃ™â€¦Ã˜Â¯Ã˜Â±Ã˜Â¨ Ã˜Â¹Ã˜Â¨Ã˜Â± Ã˜Â§Ã™â€žÃ˜Â¥Ã™â€ Ã˜ÂªÃ˜Â±Ã™â€ Ã˜Âª.',
    locationsTitle: 'Ã˜Â£Ã™â€¦Ã˜Â§Ã™Æ’Ã™â€  Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨',
    locations: ['Ã™ÂÃ™Å  Ã™â€¦Ã™â€šÃ˜Â± Ã˜Â§Ã™â€žÃ˜Â´Ã˜Â±Ã™Æ’Ã˜Â©', 'Ã™ÂÃ™Å  Ã™â€¦Ã˜Â±Ã™Æ’Ã˜Â²Ã™â€ Ã˜Â§ Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨Ã™Å ', 'Ã˜Â¹Ã˜Â¨Ã˜Â± Ã˜Â§Ã™â€žÃ˜Â¥Ã™â€ Ã˜ÂªÃ˜Â±Ã™â€ Ã˜Âª Ã˜Â¨Ã˜Â¬Ã™â€žÃ˜Â³Ã˜Â§Ã˜Âª Ã™â€¦Ã˜Â¨Ã˜Â§Ã˜Â´Ã˜Â±Ã˜Â©'],
    schedulesTitle: 'Ã˜Â¬Ã˜Â¯Ã˜Â§Ã™Ë†Ã™â€ž Ã™â€¦Ã˜Â±Ã™â€ Ã˜Â©',
    schedules: ['Ã˜ÂµÃ˜Â¨Ã˜Â§Ã˜Â­Ã™Å ', 'Ã˜Â¨Ã˜Â¹Ã˜Â¯ Ã˜Â§Ã™â€žÃ˜Â¸Ã™â€¡Ã˜Â±', 'Ã™â€¦Ã˜Â³Ã˜Â§Ã˜Â¦Ã™Å '],
  },
  process: {
    eyebrow: 'Ã˜Â¢Ã™â€žÃ™Å Ã˜Â© Ã˜Â§Ã™â€žÃ˜Â¹Ã™â€¦Ã™â€ž',
    title: 'Ã˜Â®Ã˜Â·Ã™Ë†Ã˜Â§Ã˜Âª Ã™Ë†Ã˜Â§Ã˜Â¶Ã˜Â­Ã˜Â© Ã™â€¦Ã™â€  Ã˜Â§Ã™â€žÃ˜ÂªÃ™â€šÃ™Å Ã™Å Ã™â€¦ Ã˜Â¥Ã™â€žÃ™â€° Ã˜Â§Ã™â€žÃ™â€ Ã˜ÂªÃ˜Â§Ã˜Â¦Ã˜Â¬ Ã˜Â§Ã™â€žÃ™â€šÃ˜Â§Ã˜Â¨Ã™â€žÃ˜Â© Ã™â€žÃ™â€žÃ™â€šÃ™Å Ã˜Â§Ã˜Â³',
    steps: [
      { step: '01', title: 'Ã˜ÂªÃ˜Â­Ã™â€žÃ™Å Ã™â€ž Ã˜Â§Ã™â€žÃ˜Â§Ã˜Â­Ã˜ÂªÃ™Å Ã˜Â§Ã˜Â¬', description: 'Ã™â€ Ã™â€šÃ™Å Ã™â€˜Ã™â€¦ Ã™â€¦Ã˜Â³Ã˜ÂªÃ™Ë†Ã™â€° Ã˜Â§Ã™â€žÃ™â€žÃ˜ÂºÃ˜Â© Ã™Ë†Ã˜Â§Ã˜Â­Ã˜ÂªÃ™Å Ã˜Â§Ã˜Â¬Ã˜Â§Ã˜Âª Ã˜Â§Ã™â€žÃ˜ÂªÃ™Ë†Ã˜Â§Ã˜ÂµÃ™â€ž Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¬Ã˜Â§Ã˜Â±Ã™Å  Ã™â€žÃ˜Â¯Ã™â€° Ã™ÂÃ˜Â±Ã™Å Ã™â€šÃ™Æ’Ã™â€¦.' },
      { step: '02', title: 'Ã˜ÂªÃ˜ÂµÃ™â€¦Ã™Å Ã™â€¦ Ã˜Â§Ã™â€žÃ˜Â¨Ã˜Â±Ã™â€ Ã˜Â§Ã™â€¦Ã˜Â¬', description: 'Ã™â€ Ã˜ÂµÃ™â€¦Ã™â€¦ Ã˜Â®Ã˜Â·Ã˜Â© Ã˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã™â€¦Ã˜Â®Ã˜ÂµÃ˜ÂµÃ˜Â© Ã˜ÂªÃ˜ÂªÃ™â€¦Ã˜Â§Ã˜Â´Ã™â€° Ã™â€¦Ã˜Â¹ Ã˜Â£Ã™â€¡Ã˜Â¯Ã˜Â§Ã™Â Ã˜Â´Ã˜Â±Ã™Æ’Ã˜ÂªÃ™Æ’Ã™â€¦.' },
      { step: '03', title: 'Ã˜ÂªÃ™â€ Ã™ÂÃ™Å Ã˜Â° Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨', description: 'Ã˜Â¬Ã™â€žÃ˜Â³Ã˜Â§Ã˜Âª Ã˜ÂªÃ™ÂÃ˜Â§Ã˜Â¹Ã™â€žÃ™Å Ã˜Â© Ã™Å Ã™â€šÃ™Ë†Ã˜Â¯Ã™â€¡Ã˜Â§ Ã™â€¦Ã˜Â¯Ã˜Â±Ã˜Â¨Ã™Ë†Ã™â€  Ã˜Â°Ã™Ë†Ã™Ë† Ã˜Â®Ã˜Â¨Ã˜Â±Ã˜Â©.' },
      { step: '04', title: 'Ã˜ÂªÃ™â€šÃ™Å Ã™Å Ã™â€¦ Ã˜Â§Ã™â€žÃ˜ÂªÃ™â€šÃ˜Â¯Ã™â€¦', description: 'Ã˜ÂªÃ˜ÂºÃ˜Â°Ã™Å Ã˜Â© Ã˜Â±Ã˜Â§Ã˜Â¬Ã˜Â¹Ã˜Â© Ã™â€¦Ã™â€ Ã˜ÂªÃ˜Â¸Ã™â€¦Ã˜Â© Ã™Ë†Ã˜ÂªÃ™â€šÃ˜Â§Ã˜Â±Ã™Å Ã˜Â± Ã˜ÂªÃ™â€šÃ˜Â¯Ã™â€¦ Ã™â€žÃ™â€žÃ˜Â¥Ã˜Â¯Ã˜Â§Ã˜Â±Ã˜Â©.' },
    ],
  },
  reasons: {
    eyebrow: 'Ã™â€žÃ™â€¦Ã˜Â§Ã˜Â°Ã˜Â§ Ã˜ÂªÃ˜Â®Ã˜ÂªÃ˜Â§Ã˜Â±Ã™â€ Ã˜Â§ Ã˜Â§Ã™â€žÃ˜Â´Ã˜Â±Ã™Æ’Ã˜Â§Ã˜Âª',
    title: 'Ã˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã˜Â¹Ã™â€¦Ã™â€žÃ™Å  Ã™â€¦Ã™Ë†Ã˜Â¬Ã™â€˜Ã™â€¡ Ã™â€žÃ™â€ Ã˜ÂªÃ˜Â§Ã˜Â¦Ã˜Â¬ Ã˜Â§Ã™â€žÃ˜Â£Ã˜Â¹Ã™â€¦Ã˜Â§Ã™â€ž',
    items: ['Ã™â€¦Ã˜Â¯Ã˜Â±Ã˜Â¨Ã™Ë†Ã™â€  Ã™â€¦Ã˜Â¤Ã˜Â³Ã˜Â³Ã™Å Ã™Ë†Ã™â€  Ã˜Â°Ã™Ë†Ã™Ë† Ã˜Â®Ã˜Â¨Ã˜Â±Ã˜Â©', 'Ã˜Â¨Ã˜Â±Ã˜Â§Ã™â€¦Ã˜Â¬ Ã™â€¦Ã˜Â®Ã˜ÂµÃ˜ÂµÃ˜Â© Ã™â€žÃ™Æ’Ã™â€ž Ã™â€¦Ã˜Â¤Ã˜Â³Ã˜Â³Ã˜Â©', 'Ã˜Â¬Ã˜Â¯Ã˜Â§Ã™Ë†Ã™â€ž Ã™â€¦Ã˜Â±Ã™â€ Ã˜Â© Ã™â€žÃ™â€žÃ™â€¦Ã™â€¡Ã™â€ Ã™Å Ã™Å Ã™â€  Ã˜Â§Ã™â€žÃ™â€¦Ã˜Â´Ã˜ÂºÃ™Ë†Ã™â€žÃ™Å Ã™â€ ', 'Ã™â€¦Ã™â€ Ã™â€¡Ã˜Â¬ Ã˜Â¹Ã™â€¦Ã™â€žÃ™Å  Ã™â€¦Ã™Ë†Ã˜Â¬Ã™â€¡ Ã™â€žÃ˜Â¨Ã™Å Ã˜Â¦Ã˜Â© Ã˜Â§Ã™â€žÃ˜Â¹Ã™â€¦Ã™â€ž', 'Ã™â€¦Ã˜ÂªÃ˜Â§Ã˜Â¨Ã˜Â¹Ã˜Â© Ã™Ë†Ã™â€šÃ™Å Ã˜Â§Ã˜Â³ Ã™â€žÃ™â€žÃ˜ÂªÃ™â€šÃ˜Â¯Ã™â€¦'],
  },
  trusted: {
    eyebrow: 'Ã˜Â¬Ã™â€¡Ã˜Â§Ã˜Âª Ã˜ÂªÃ˜Â«Ã™â€š Ã˜Â¨Ã˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨Ã™â€ Ã˜Â§',
    title: 'Ã™â€¦Ã™Ë†Ã˜Â«Ã™Ë†Ã™â€šÃ™Ë†Ã™â€  Ã™â€žÃ˜Â¯Ã™â€° Ã˜Â¬Ã™â€¡Ã˜Â§Ã˜Âª Ã˜Â±Ã˜Â§Ã˜Â¦Ã˜Â¯Ã˜Â©',
    description: 'Ã˜ÂªÃ˜Â«Ã™â€š Ã™â€¦Ã™â€ Ã˜Â¸Ã™â€¦Ã˜Â§Ã˜Âª Ã™ÂÃ™Å  Ã™â€¦Ã˜Â®Ã˜ÂªÃ™â€žÃ™Â Ã˜Â£Ã™â€ Ã˜Â­Ã˜Â§Ã˜Â¡ Ã˜Â§Ã™â€žÃ˜Â¥Ã™â€¦Ã˜Â§Ã˜Â±Ã˜Â§Ã˜Âª Ã˜Â¨Ã˜Â¨Ã˜Â±Ã˜Â§Ã™â€¦Ã˜Â¬Ã™â€ Ã˜Â§ Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨Ã™Å Ã˜Â© Ã˜Â§Ã™â€žÃ™â€žÃ˜ÂºÃ™Ë†Ã™Å Ã˜Â© Ã™â€žÃ˜ÂªÃ˜Â¹Ã˜Â²Ã™Å Ã˜Â² Ã˜Â§Ã™â€žÃ˜ÂªÃ™Ë†Ã˜Â§Ã˜ÂµÃ™â€ž Ã™Ë†Ã˜Â§Ã™â€žÃ™â€¦Ã™â€¡Ã˜Â§Ã˜Â±Ã˜Â§Ã˜Âª Ã˜Â§Ã™â€žÃ™â€¦Ã™â€¡Ã™â€ Ã™Å Ã˜Â© Ã™ÂÃ™Å  Ã˜Â¨Ã™Å Ã˜Â¦Ã˜Â§Ã˜Âª Ã˜Â§Ã™â€žÃ˜Â¹Ã™â€¦Ã™â€ž Ã™â€¦Ã˜ÂªÃ˜Â¹Ã˜Â¯Ã˜Â¯Ã˜Â© Ã˜Â§Ã™â€žÃ˜Â«Ã™â€šÃ˜Â§Ã™ÂÃ˜Â§Ã˜Âª.',
    logos: ['Ã™â€¦Ã˜Â¬Ã™â€¦Ã™Ë†Ã˜Â¹Ã˜Â© Ã˜Â¶Ã™Å Ã˜Â§Ã™ÂÃ˜Â©', 'Ã˜Â´Ã˜Â±Ã™Å Ã™Æ’ Ã˜Â·Ã™Å Ã˜Â±Ã˜Â§Ã™â€ ', 'Ã˜Â´Ã˜Â¨Ã™Æ’Ã˜Â© Ã˜ÂªÃ˜Â¬Ã˜Â²Ã˜Â¦Ã˜Â©', 'Ã™â€¦Ã™â€ Ã˜Â¸Ã™Ë†Ã™â€¦Ã˜Â© Ã˜Â±Ã˜Â¹Ã˜Â§Ã™Å Ã˜Â© Ã˜ÂµÃ˜Â­Ã™Å Ã˜Â©', 'Ã˜Â´Ã˜Â±Ã™Å Ã™Æ’ Ã™â€¦Ã˜ÂµÃ˜Â±Ã™ÂÃ™Å ', 'Ã™â€šÃ˜Â·Ã˜Â§Ã˜Â¹ Ã˜Â¹Ã˜Â§Ã™â€¦'],
  },
  caseStudy: {
    eyebrow: 'Ã™â€¦Ã˜Â«Ã˜Â§Ã™â€ž Ã˜Â¹Ã™â€žÃ™â€° Ã˜Â¨Ã˜Â±Ã™â€ Ã˜Â§Ã™â€¦Ã˜Â¬ Ã™â€¦Ã˜Â¤Ã˜Â³Ã˜Â³Ã™Å ',
    title: 'Ã˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã˜Â§Ã™â€žÃ˜Â¥Ã™â€ Ã˜Â¬Ã™â€žÃ™Å Ã˜Â²Ã™Å Ã˜Â© Ã™â€žÃ™â€žÃ˜Â£Ã˜Â¹Ã™â€¦Ã˜Â§Ã™â€ž Ã™â€žÃ™ÂÃ˜Â±Ã™â€š Ã˜Â®Ã˜Â¯Ã™â€¦Ã˜Â© Ã˜Â§Ã™â€žÃ˜Â¹Ã™â€¦Ã™â€žÃ˜Â§Ã˜Â¡',
    items: [
      { label: 'Ã˜Â§Ã™â€žÃ˜Â´Ã˜Â±Ã™Æ’Ã˜Â©', value: 'Ã™â€¦Ã˜Â¬Ã™â€¦Ã™Ë†Ã˜Â¹Ã˜Â© Ã˜Â¶Ã™Å Ã˜Â§Ã™ÂÃ˜Â© Ã˜Â¥Ã™â€šÃ™â€žÃ™Å Ã™â€¦Ã™Å Ã˜Â© Ã™ÂÃ™Å  Ã˜Â¯Ã˜Â¨Ã™Å ' },
      { label: 'Ã˜Â§Ã™â€žÃ™â€¡Ã˜Â¯Ã™Â', value: 'Ã˜ÂªÃ˜Â­Ã˜Â³Ã™Å Ã™â€  Ã˜Â§Ã™â€žÃ˜ÂªÃ™Ë†Ã˜Â§Ã˜ÂµÃ™â€ž Ã˜Â¨Ã™Å Ã™â€  Ã™â€¦Ã™Ë†Ã˜Â¸Ã™ÂÃ™Å  Ã˜Â§Ã™â€žÃ™Ë†Ã˜Â§Ã˜Â¬Ã™â€¡Ã˜Â© Ã™Ë†Ã˜Â§Ã™â€žÃ˜Â¶Ã™Å Ã™Ë†Ã™Â Ã˜Â§Ã™â€žÃ˜Â¯Ã™Ë†Ã™â€žÃ™Å Ã™Å Ã™â€ .' },
      { label: 'Ã™â€ Ã™â€¦Ã˜Â· Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨', value: 'Ã˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã˜Â¬Ã™â€¦Ã˜Â§Ã˜Â¹Ã™Å  (6 Ã™â€¦Ã˜Â´Ã˜Â§Ã˜Â±Ã™Æ’Ã™Å Ã™â€ )' },
      { label: 'Ã™â€¦Ã˜Â¯Ã˜Â© Ã˜Â§Ã™â€žÃ˜Â¨Ã˜Â±Ã™â€ Ã˜Â§Ã™â€¦Ã˜Â¬', value: '8 Ã˜Â£Ã˜Â³Ã˜Â§Ã˜Â¨Ã™Å Ã˜Â¹' },
    ],
    focusTitle: 'Ã™â€¦Ã˜Â¬Ã˜Â§Ã™â€žÃ˜Â§Ã˜Âª Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â±Ã™Æ’Ã™Å Ã˜Â²',
    focusAreas: ['Ã˜Â§Ã™â€žÃ˜ÂªÃ™Ë†Ã˜Â§Ã˜ÂµÃ™â€ž Ã™â€¦Ã˜Â¹ Ã˜Â§Ã™â€žÃ˜Â¶Ã™Å Ã™Ë†Ã™Â', 'Ã˜Â§Ã™â€žÃ˜ÂªÃ™ÂÃ˜Â§Ã˜Â¹Ã™â€ž Ã˜Â§Ã™â€žÃ˜Â®Ã˜Â¯Ã™â€¦Ã™Å ', 'Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¹Ã˜Â§Ã™â€¦Ã™â€ž Ã™â€¦Ã˜Â¹ Ã˜Â§Ã™â€žÃ˜Â´Ã™Æ’Ã˜Â§Ã™Ë†Ã™â€°', 'Ã˜Â§Ã™â€žÃ˜Â¨Ã˜Â±Ã™Å Ã˜Â¯ Ã˜Â§Ã™â€žÃ˜Â¥Ã™â€žÃ™Æ’Ã˜ÂªÃ˜Â±Ã™Ë†Ã™â€ Ã™Å  Ã˜Â§Ã™â€žÃ™â€¦Ã™â€¡Ã™â€ Ã™Å ', 'Ã˜Â§Ã™â€žÃ˜ÂªÃ™Ë†Ã˜Â§Ã˜ÂµÃ™â€ž Ã˜Â¨Ã™Å Ã™â€  Ã˜Â§Ã™â€žÃ˜Â«Ã™â€šÃ˜Â§Ã™ÂÃ˜Â§Ã˜Âª'],
    resultTitle: 'Ã˜Â§Ã™â€žÃ™â€ Ã˜ÂªÃ™Å Ã˜Â¬Ã˜Â©',
    result: 'Ã˜ÂªÃ˜Â­Ã˜Â³Ã™â€ Ã˜Âª Ã˜Â«Ã™â€šÃ˜Â© Ã˜Â§Ã™â€žÃ™â€¦Ã™Ë†Ã˜Â¸Ã™ÂÃ™Å Ã™â€  Ã™ÂÃ™Å  Ã˜Â§Ã™â€žÃ˜ÂªÃ™Ë†Ã˜Â§Ã˜ÂµÃ™â€ž Ã˜Â¨Ã˜Â§Ã™â€žÃ™â€žÃ˜ÂºÃ˜Â© Ã˜Â§Ã™â€žÃ˜Â¥Ã™â€ Ã˜Â¬Ã™â€žÃ™Å Ã˜Â²Ã™Å Ã˜Â© Ã™Ë†Ã˜Â§Ã™â€žÃ˜ÂªÃ™ÂÃ˜Â§Ã˜Â¹Ã™â€ž Ã™â€¦Ã˜Â¹ Ã˜Â§Ã™â€žÃ˜Â¹Ã™â€¦Ã™â€žÃ˜Â§Ã˜Â¡Ã˜Å’ Ã™â€¦Ã™â€¦Ã˜Â§ Ã˜Â£Ã˜Â³Ã™â€¡Ã™â€¦ Ã™ÂÃ™Å  Ã˜Â±Ã™ÂÃ˜Â¹ Ã˜Â±Ã˜Â¶Ã˜Â§ Ã˜Â§Ã™â€žÃ˜Â¶Ã™Å Ã™Ë†Ã™Â.',
  },
  proposal: {
    eyebrow: 'Ã˜Â§Ã˜Â·Ã™â€žÃ˜Â¨ Ã˜Â¹Ã˜Â±Ã˜Â¶ Ã˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã™â€¦Ã˜Â¤Ã˜Â³Ã˜Â³Ã™Å ',
    title: 'Ã˜Â£Ã˜Â®Ã˜Â¨Ã˜Â±Ã™â€ Ã˜Â§ Ã˜Â¹Ã™â€  Ã˜Â§Ã˜Â­Ã˜ÂªÃ™Å Ã˜Â§Ã˜Â¬Ã˜Â§Ã˜ÂªÃ™Æ’Ã™â€¦ Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨Ã™Å Ã˜Â©',
    description: 'Ã˜Â´Ã˜Â§Ã˜Â±Ã™Æ’Ã™â€ Ã˜Â§ Ã˜Â¨Ã˜Â¹Ã˜Â¶ Ã˜Â§Ã™â€žÃ˜ÂªÃ™ÂÃ˜Â§Ã˜ÂµÃ™Å Ã™â€ž Ã˜Â¹Ã™â€  Ã™â€¦Ã™â€ Ã˜Â¸Ã™â€¦Ã˜ÂªÃ™Æ’Ã™â€¦ Ã™Ë†Ã˜Â³Ã™Å Ã™ÂÃ˜Â¹Ã˜Â¯ Ã™ÂÃ˜Â±Ã™Å Ã™â€šÃ™â€ Ã˜Â§ Ã˜Â¹Ã˜Â±Ã˜Â¶ Ã˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã™â€¦Ã˜Â®Ã˜ÂµÃ˜ÂµÃ™â€¹Ã˜Â§ Ã™â€žÃ™Æ’Ã™â€¦.',
    submitLabel: 'Ã˜Â§Ã˜Â·Ã™â€žÃ˜Â¨ Ã˜Â§Ã™â€žÃ˜Â¹Ã˜Â±Ã˜Â¶',
  },
  finalCta: {
    eyebrow: 'Ã˜Â§Ã™â€žÃ˜Â®Ã˜Â·Ã™Ë†Ã˜Â© Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â§Ã™â€žÃ™Å Ã˜Â©',
    title: 'Ã™â€¡Ã™â€ž Ã˜Â£Ã™â€ Ã˜ÂªÃ™â€¦ Ã™â€¦Ã˜Â³Ã˜ÂªÃ˜Â¹Ã˜Â¯Ã™Ë†Ã™â€  Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã™ÂÃ˜Â±Ã™Å Ã™â€šÃ™Æ’Ã™â€¦Ã˜Å¸',
    description: 'Ã˜ÂªÃ™Ë†Ã˜Â§Ã˜ÂµÃ™â€žÃ™Ë†Ã˜Â§ Ã™â€¦Ã˜Â¹Ã™â€ Ã˜Â§ Ã˜Â§Ã™â€žÃ™Å Ã™Ë†Ã™â€¦ Ã™â€žÃ™â€¦Ã™â€ Ã˜Â§Ã™â€šÃ˜Â´Ã˜Â© Ã˜Â§Ã˜Â­Ã˜ÂªÃ™Å Ã˜Â§Ã˜Â¬Ã˜Â§Ã˜ÂªÃ™Æ’Ã™â€¦ Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨Ã™Å Ã˜Â© Ã™Ë†Ã˜Â§Ã™â€žÃ˜Â­Ã˜ÂµÃ™Ë†Ã™â€ž Ã˜Â¹Ã™â€žÃ™â€° Ã˜Â¹Ã˜Â±Ã˜Â¶ Ã˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã™â€¦Ã˜Â¤Ã˜Â³Ã˜Â³Ã™Å  Ã™â€¦Ã˜Â®Ã˜ÂµÃ˜Âµ.',
    primaryLabel: 'Ã˜Â§Ã˜Â·Ã™â€žÃ˜Â¨ Ã˜Â¹Ã˜Â±Ã˜Â¶ Ã˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã™â€¦Ã˜Â¤Ã˜Â³Ã˜Â³Ã™Å ',
    secondaryLabel: 'Ã˜ÂªÃ™Ë†Ã˜Â§Ã˜ÂµÃ™â€ž Ã™â€¦Ã˜Â¹Ã™â€ Ã˜Â§',
  },
};

function ensureArray(value, fallback) {
  return Array.isArray(value) ? value : fallback;
}

function normalizeCorporateContent(rawContent, lang) {
  const fallbackByLang = lang === 'ar' ? fallbackContentAr : fallbackContent;
  const source = rawContent && typeof rawContent === 'object' ? rawContent : {};
  return {
    hero: {
      ...fallbackByLang.hero,
      ...(source.hero || {}),
    },
    audience: {
      ...fallbackByLang.audience,
      ...(source.audience || {}),
      sectors: ensureArray(source.audience?.sectors, fallbackByLang.audience.sectors),
    },
    programs: {
      ...fallbackByLang.programs,
      ...(source.programs || {}),
      items: ensureArray(source.programs?.items, fallbackByLang.programs.items),
    },
    benefits: {
      ...fallbackByLang.benefits,
      ...(source.benefits || {}),
      items: ensureArray(source.benefits?.items, fallbackByLang.benefits.items),
    },
    formats: {
      ...fallbackByLang.formats,
      ...(source.formats || {}),
      items: ensureArray(source.formats?.items, fallbackByLang.formats.items),
    },
    delivery: {
      ...fallbackByLang.delivery,
      ...(source.delivery || {}),
      locations: ensureArray(source.delivery?.locations, fallbackByLang.delivery.locations),
      schedules: ensureArray(source.delivery?.schedules, fallbackByLang.delivery.schedules),
    },
    process: {
      ...fallbackByLang.process,
      ...(source.process || {}),
      steps: ensureArray(source.process?.steps, fallbackByLang.process.steps),
    },
    reasons: {
      ...fallbackByLang.reasons,
      ...(source.reasons || {}),
      items: ensureArray(source.reasons?.items, fallbackByLang.reasons.items),
    },
    trusted: {
      ...fallbackByLang.trusted,
      ...(source.trusted || {}),
      logos: ensureArray(source.trusted?.logos, fallbackByLang.trusted.logos),
    },
    caseStudy: {
      ...fallbackByLang.caseStudy,
      ...(source.caseStudy || {}),
      items: ensureArray(source.caseStudy?.items, fallbackByLang.caseStudy.items),
      focusAreas: ensureArray(source.caseStudy?.focusAreas, fallbackByLang.caseStudy.focusAreas),
    },
    proposal: {
      ...fallbackByLang.proposal,
      ...(source.proposal || {}),
    },
    finalCta: {
      ...fallbackByLang.finalCta,
      ...(source.finalCta || {}),
    },
  };
}

function BulletList({ items, dark = false }) {
  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div key={item} className="flex items-start gap-3">
          <Check className="mt-0.5 h-4 w-4 text-[#6c8bc6]" />
          <span className={`text-sm leading-7 ${dark ? 'text-[#e6e6e6]/76' : 'text-[#231f20]/74 dark:text-[#e6e6e6]/74'}`}>{item}</span>
        </div>
      ))}
    </div>
  );
}

export default function CorporatePage() {
  const { lang } = useLang();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [section, setSection] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    contact_person: '',
    work_email: '',
    phone: '',
    country_code: '+971',
    employees_range: '',
    training_language: '',
    training_type: '',
    preferred_location: '',
    message: '',
  });

  useEffect(() => {
    api.get('/content/corporate').then((res) => setSection(res.data || null)).catch(() => setSection(null));
  }, [lang]);

  useEffect(() => {
    api.get('/content/teachers')
      .then((res) => setTeachers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTeachers([]));
  }, [lang]);

  const content = useMemo(() => normalizeCorporateContent(section?.content, lang), [lang, section]);
  const programs = useMemo(() => (content.programs.items || []).map((item) => ({ ...item, Icon: iconMap[item.icon] || BriefcaseBusiness })), [content.programs.items]);
  const benefits = useMemo(() => (content.benefits.items || []).map((item) => ({ ...item, Icon: iconMap[item.icon] || Users })), [content.benefits.items]);
  const formats = useMemo(() => (content.formats.items || []).map((item) => ({ ...item, Icon: iconMap[item.icon] || Users })), [content.formats.items]);
  const visibleTeachers = useMemo(() => {
    return teachers
      .map((teacher) => ({
        ...teacher,
        image_url: String(teacher?.image_url || '').trim(),
        video_url: String(teacher?.video_url || '').trim(),
      }))
      .filter((teacher) => teacher?.name || teacher?.title || teacher?.short_bio || teacher?.image_url || teacher?.video_url)
      .slice(0, 4);
  }, [teachers]);
  const formCopy = useMemo(() => ({
    companyName: pickText(lang, 'Ã˜Â§Ã˜Â³Ã™â€¦ Ã˜Â§Ã™â€žÃ˜Â´Ã˜Â±Ã™Æ’Ã˜Â©', 'Company name'),
    contactPerson: pickText(lang, 'Ã˜Â§Ã™â€žÃ˜Â´Ã˜Â®Ã˜Âµ Ã˜Â§Ã™â€žÃ™â€¦Ã˜Â³Ã˜Â¤Ã™Ë†Ã™â€ž', 'Contact person'),
    workEmail: pickText(lang, 'Ã˜Â§Ã™â€žÃ˜Â¨Ã˜Â±Ã™Å Ã˜Â¯ Ã˜Â§Ã™â€žÃ™â€¦Ã™â€¡Ã™â€ Ã™Å ', 'Work email'),
    code: pickText(lang, 'Ã˜Â§Ã™â€žÃ˜Â±Ã™â€¦Ã˜Â²', 'Code'),
    phone: pickText(lang, 'Ã˜Â±Ã™â€šÃ™â€¦ Ã˜Â§Ã™â€žÃ™â€¡Ã˜Â§Ã˜ÂªÃ™Â', 'Phone number'),
    employeeCount: pickText(lang, 'Ã˜Â¹Ã˜Â¯Ã˜Â¯ Ã˜Â§Ã™â€žÃ™â€¦Ã™Ë†Ã˜Â¸Ã™ÂÃ™Å Ã™â€  Ã˜Â§Ã™â€žÃ™â€¦Ã˜Â·Ã™â€žÃ™Ë†Ã˜Â¨ Ã˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨Ã™â€¡Ã™â€¦', 'Number of employees to be trained'),
    selectRange: pickText(lang, 'Ã˜Â§Ã˜Â®Ã˜ÂªÃ˜Â± Ã˜Â§Ã™â€žÃ™â€ Ã˜Â·Ã˜Â§Ã™â€š', 'Select range'),
    trainingLanguage: pickText(lang, 'Ã™â€žÃ˜ÂºÃ˜Â© Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨', 'Training language'),
    selectLanguage: pickText(lang, 'Ã˜Â§Ã˜Â®Ã˜ÂªÃ˜Â± Ã˜Â§Ã™â€žÃ™â€žÃ˜ÂºÃ˜Â©', 'Select language'),
    trainingType: pickText(lang, 'Ã™â€ Ã™Ë†Ã˜Â¹ Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨', 'Training type'),
    selectType: pickText(lang, 'Ã˜Â§Ã˜Â®Ã˜ÂªÃ˜Â± Ã˜Â§Ã™â€žÃ™â€ Ã™Ë†Ã˜Â¹', 'Select type'),
    location: pickText(lang, 'Ã™â€¦Ã™Æ’Ã˜Â§Ã™â€  Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã˜Â§Ã™â€žÃ™â€¦Ã™ÂÃ˜Â¶Ã™â€ž', 'Preferred training location'),
    selectLocation: pickText(lang, 'Ã˜Â§Ã˜Â®Ã˜ÂªÃ˜Â± Ã˜Â§Ã™â€žÃ™â€¦Ã™Æ’Ã˜Â§Ã™â€ ', 'Select location'),
    message: pickText(lang, 'Ã˜Â§Ã™â€žÃ˜Â±Ã˜Â³Ã˜Â§Ã™â€žÃ˜Â© / Ã˜Â£Ã™â€¡Ã˜Â¯Ã˜Â§Ã™Â Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨', 'Message / training goals'),
    requiredFields: pickText(lang, 'Ã™Å Ã˜Â±Ã˜Â¬Ã™â€° Ã˜ÂªÃ˜Â¹Ã˜Â¨Ã˜Â¦Ã˜Â© Ã˜Â§Ã™â€žÃ˜Â­Ã™â€šÃ™Ë†Ã™â€ž Ã˜Â§Ã™â€žÃ™â€¦Ã˜Â·Ã™â€žÃ™Ë†Ã˜Â¨Ã˜Â©.', 'Please fill the required fields.'),
    requestSent: pickText(lang, 'Ã˜ÂªÃ™â€¦ Ã˜Â¥Ã˜Â±Ã˜Â³Ã˜Â§Ã™â€ž Ã˜Â·Ã™â€žÃ˜Â¨ Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã˜Â§Ã™â€žÃ™â€¦Ã˜Â¤Ã˜Â³Ã˜Â³Ã™Å  Ã˜Â¨Ã™â€ Ã˜Â¬Ã˜Â§Ã˜Â­.', 'Your corporate training request has been sent.'),
    requestFailed: pickText(lang, 'Ã˜ÂªÃ˜Â¹Ã˜Â°Ã˜Â± Ã˜Â¥Ã˜Â±Ã˜Â³Ã˜Â§Ã™â€ž Ã˜Â§Ã™â€žÃ˜Â·Ã™â€žÃ˜Â¨ Ã˜Â§Ã™â€žÃ˜Â¢Ã™â€ .', 'We could not send your request right now.'),
    notProvided: pickText(lang, 'Ã˜ÂºÃ™Å Ã˜Â± Ã™â€¦Ã˜Â°Ã™Æ’Ã™Ë†Ã˜Â±', 'Not provided'),
    contactPersonNote: pickText(lang, 'Ã˜Â§Ã™â€žÃ˜Â´Ã˜Â®Ã˜Âµ Ã˜Â§Ã™â€žÃ™â€¦Ã˜Â³Ã˜Â¤Ã™Ë†Ã™â€ž', 'Contact person'),
    employeesNote: pickText(lang, 'Ã˜Â¹Ã˜Â¯Ã˜Â¯ Ã˜Â§Ã™â€žÃ™â€¦Ã™Ë†Ã˜Â¸Ã™ÂÃ™Å Ã™â€ ', 'Employees to be trained'),
    trainingLanguageNote: pickText(lang, 'Ã™â€žÃ˜ÂºÃ˜Â© Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨', 'Training language'),
    trainingTypeNote: pickText(lang, 'Ã™â€ Ã™Ë†Ã˜Â¹ Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨', 'Training type'),
    locationNote: pickText(lang, 'Ã™â€¦Ã™Æ’Ã˜Â§Ã™â€  Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨ Ã˜Â§Ã™â€žÃ™â€¦Ã™ÂÃ˜Â¶Ã™â€ž', 'Preferred training location'),
    goalsNote: pickText(lang, 'Ã˜Â£Ã™â€¡Ã˜Â¯Ã˜Â§Ã™Â Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨', 'Training goals'),
    english: pickText(lang, 'Ã˜Â§Ã™â€žÃ˜Â¥Ã™â€ Ã˜Â¬Ã™â€žÃ™Å Ã˜Â²Ã™Å Ã˜Â©', 'English'),
    arabic: pickText(lang, 'Ã˜Â§Ã™â€žÃ˜Â¹Ã˜Â±Ã˜Â¨Ã™Å Ã˜Â©', 'Arabic'),
    group: pickText(lang, 'Ã˜Â¬Ã™â€¦Ã˜Â§Ã˜Â¹Ã™Å ', 'Group'),
    executive: pickText(lang, 'Ã˜ÂªÃ™â€ Ã™ÂÃ™Å Ã˜Â°Ã™Å ', 'Executive'),
    intensive: pickText(lang, 'Ã™â€¦Ã™Æ’Ã˜Â«Ã™Â', 'Intensive'),
    company: pickText(lang, 'Ã™ÂÃ™Å  Ã˜Â§Ã™â€žÃ˜Â´Ã˜Â±Ã™Æ’Ã˜Â©', 'Company'),
    trainingCenter: pickText(lang, 'Ã™â€¦Ã˜Â±Ã™Æ’Ã˜Â² Ã˜Â§Ã™â€žÃ˜ÂªÃ˜Â¯Ã˜Â±Ã™Å Ã˜Â¨', 'Training Center'),
    online: pickText(lang, 'Ã˜Â¹Ã˜Â¨Ã˜Â± Ã˜Â§Ã™â€žÃ˜Â¥Ã™â€ Ã˜ÂªÃ˜Â±Ã™â€ Ã˜Âª', 'Online'),
    sending: pickText(lang, 'Ã˜Â¬Ã˜Â§Ã˜Â±Ã™Â Ã˜Â§Ã™â€žÃ˜Â¥Ã˜Â±Ã˜Â³Ã˜Â§Ã™â€ž...', 'Sending...'),
  }), [lang]);

  const sectionGlow = {
    backgroundImage: isDark
      ? 'radial-gradient(circle at 18% 10%, rgba(108,139,198,0.24), transparent 26%), radial-gradient(circle at 85% 8%, rgba(230,230,230,0.08), transparent 24%)'
      : 'radial-gradient(circle at 18% 10%, rgba(108,139,198,0.18), transparent 26%), radial-gradient(circle at 85% 8%, rgba(35,31,32,0.08), transparent 24%)',
  };

  const handleChange = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const scrollToProposal = () => {
    const target = document.getElementById('request-proposal');
    if (!target) return;
    const navbarOffset = 110;
    const targetPosition = target.getBoundingClientRect().top + window.scrollY - navbarOffset;
    window.scrollTo({ top: targetPosition, behavior: 'smooth' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.company_name || !form.contact_person || !form.work_email || !form.phone || !form.employees_range || !form.training_language || !form.training_type || !form.preferred_location) {
      toast.error(formCopy.requiredFields);
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/contact', {
        lead_type: 'corporate',
        company_name: form.company_name,
        work_email: form.work_email,
        phone: form.phone,
        country_code: form.country_code,
        employees_range: form.employees_range,
        notes: [
          `${formCopy.contactPersonNote}: ${form.contact_person}`,
          `${formCopy.employeesNote}: ${form.employees_range}`,
          `${formCopy.trainingLanguageNote}: ${form.training_language}`,
          `${formCopy.trainingTypeNote}: ${form.training_type}`,
          `${formCopy.locationNote}: ${form.preferred_location}`,
          '',
          `${formCopy.goalsNote}:`,
          form.message || formCopy.notProvided,
        ].join('\n'),
      });
      trackEvent('corporate_proposal_submit_success', {
        form_id: 'corporate_proposal_form',
        form_type: 'corporate_proposal',
        source_page: '/corporate',
        source_component: 'CorporatePage',
        route: '/corporate',
        language: lang,
        contact_type: 'corporate',
        status: 'success',
      });
      setForm({
        company_name: '',
        contact_person: '',
        work_email: '',
        phone: '',
        country_code: '+971',
        employees_range: '',
        training_language: '',
        training_type: '',
        preferred_location: '',
        message: '',
      });
      navigate('/thank-you/inquiry-received', {
        state: {
          form_type: 'corporate_proposal',
          source_page: '/corporate',
          source_component: 'CorporatePage',
          verified_submission: true,
        },
      });
    } catch {
      toast.error(formCopy.requestFailed);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="corporate-page" className="bf-page-canvas bf-corporate-page bg-[#e6e6e6] text-[#231f20] dark:bg-[#231f20] dark:text-[#e6e6e6]">
      <section className="bf-editorial-page-hero relative overflow-hidden px-4 py-14 sm:px-6 lg:px-8 lg:py-20" style={sectionGlow}>
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(35,31,32,0),rgba(35,31,32,0.12))] dark:bg-[linear-gradient(180deg,rgba(35,31,32,0.12),rgba(35,31,32,0.36))]" />
        <div className="relative mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1.02fr_0.98fr] lg:items-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0.04} className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#6c8bc6]">{content.hero.eyebrow}</p>
            <h1 className="mt-4 text-4xl font-semibold tracking-[-0.04em] sm:text-5xl lg:text-[4.2rem]">{content.hero.title}</h1>
            <p className="mt-5 max-w-2xl text-lg leading-8 text-[#231f20]/74 dark:text-[#e6e6e6]/74">{content.hero.subtitle}</p>
            <p className="mt-5 max-w-2xl text-base leading-8 text-[#231f20]/72 dark:text-[#e6e6e6]/72">{content.hero.intro}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button type="button" className="rounded-full bg-[#6c8bc6] px-7 text-white hover:bg-[#6c8bc6]" onClick={scrollToProposal}>{content.hero.primaryCtaLabel} <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.12 }} className="overflow-hidden rounded-[2rem] border border-[#231f20]/10 bg-white/80 shadow-[0_28px_70px_-42px_rgba(15,23,42,0.45)] dark:border-white/10 dark:bg-white/6 dark:shadow-none">
            {section?.image_url ? (
              <img src={buildImageUrl(section.image_url)} alt={content.hero.title} className="h-[320px] w-full object-cover sm:h-[420px]" />
            ) : (
              <div className="flex h-[320px] items-center justify-center bg-[linear-gradient(135deg,rgba(108,139,198,0.18),rgba(35,31,32,0.08))] dark:bg-[linear-gradient(135deg,rgba(108,139,198,0.24),rgba(255,255,255,0.06))] sm:h-[420px]">
                <Building2 className="h-16 w-16 text-[#6c8bc6]" />
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <section className="px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-7xl grid gap-8 xl:grid-cols-[0.42fr_0.58fr]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#6c8bc6]">{content.audience.eyebrow}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">{content.audience.title}</h2>
            <p className="mt-4 text-base leading-8 text-[#231f20]/72 dark:text-[#e6e6e6]/72">{content.audience.description}</p>
            <p className="mt-4 text-base leading-8 text-[#231f20]/72 dark:text-[#e6e6e6]/72">{content.audience.deliveryNote}</p>
          </div>
          <Card className="rounded-[2rem] border border-[#231f20]/10 bg-white shadow-[0_24px_60px_-38px_rgba(15,23,42,0.26)] dark:border-white/10 dark:bg-[#231f20] dark:shadow-none">
            <CardContent className="grid gap-3 p-6 sm:grid-cols-2 sm:p-8">
              {content.audience.sectors.map((sector) => (
                <div key={sector} className="flex items-center gap-3 rounded-[1.2rem] bg-[#e6e6e6] px-4 py-3 dark:bg-white/5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#231f20] text-white dark:bg-white/10"><Landmark className="h-4 w-4" /></div>
                  <span className="text-sm font-medium">{sector}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="bg-white px-4 py-14 sm:px-6 lg:px-8 lg:py-20 dark:bg-[#231f20]">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#6c8bc6]">{content.programs.eyebrow}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">{content.programs.title}</h2>
          </div>
          <div className="grid gap-5 lg:grid-cols-2">
            {programs.map(({ Icon, ...program }, index) => (
              <motion.div key={program.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-50px' }} transition={{ delay: index * 0.08, duration: 0.45 }}>
                <Card className="h-full rounded-[2rem] border border-[#231f20]/10 bg-[#e6e6e6] dark:border-white/10 dark:bg-white/5">
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#231f20] text-white dark:bg-white/10"><Icon className="h-5 w-5" /></div>
                    <h3 className="mt-5 text-2xl font-semibold">{program.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-[#231f20]/74 dark:text-[#e6e6e6]/74">{program.description}</p>
                    {program.intro ? <p className="mt-4 text-sm font-medium text-[#6c8bc6]">{program.intro}</p> : null}
                    <div className="mt-5"><BulletList items={program.bullets} /></div>
                    <p className="mt-6 rounded-[1.4rem] bg-white px-5 py-4 text-sm leading-7 text-[#231f20]/72 dark:bg-[#231f20] dark:text-[#e6e6e6]/72">{program.footer}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#6c8bc6]">{content.benefits.eyebrow}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">{content.benefits.title}</h2>
          </div>
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-5">
            {benefits.map(({ title, description, Icon }, index) => (
              <motion.div key={title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-50px' }} transition={{ delay: index * 0.06, duration: 0.45 }}>
                <Card className="h-full rounded-[1.8rem] border border-[#231f20]/10 bg-white dark:border-white/10 dark:bg-[#231f20]">
                  <CardContent className="p-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#231f20] text-white dark:bg-white/10"><Icon className="h-5 w-5" /></div>
                    <h3 className="mt-5 text-lg font-semibold">{title}</h3>
                    <p className="mt-3 text-sm leading-7 text-[#231f20]/72 dark:text-[#e6e6e6]/72">{description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#231f20] px-4 py-14 text-white sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#e6e6e6]">{content.formats.eyebrow}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">{content.formats.title}</h2>
          </div>
          <div className="grid gap-5 lg:grid-cols-3">
            {formats.map(({ title, description, bullets, Icon }, index) => (
              <motion.div key={title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-50px' }} transition={{ delay: index * 0.08, duration: 0.45 }}>
                <Card className="h-full rounded-[1.9rem] border border-white/10 bg-white/6 text-white shadow-none backdrop-blur">
                  <CardContent className="p-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/12"><Icon className="h-5 w-5 text-[#6c8bc6]" /></div>
                    <h3 className="mt-5 text-xl font-semibold">{title}</h3>
                    <p className="mt-3 text-sm leading-7 text-[#e6e6e6]/74">{description}</p>
                    <div className="mt-5"><BulletList items={bullets} dark /></div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <TeacherShowcaseSection
        teachers={visibleTeachers}
        lang={lang}
        className="bf-corporate-teacher-section"
        eyebrow={pickText(lang, '\u0641\u0631\u064a\u0642 \u0627\u0644\u062a\u062f\u0631\u064a\u0628', 'Training Faculty')}
        title={pickText(lang, '\u0645\u062f\u0631\u0651\u0633\u0648\u0646 \u064a\u0631\u0628\u0637\u0648\u0646 \u0627\u0644\u0644\u063a\u0629 \u0628\u0633\u064a\u0627\u0642 \u0627\u0644\u0639\u0645\u0644', 'Instructors who connect language to business context')}
        subtitle={pickText(
          lang,
          '\u064a\u062a\u0648\u0644\u0649 \u0627\u0644\u062a\u062f\u0631\u064a\u0628 \u0645\u062f\u0631\u0651\u0633\u0648\u0646 \u0630\u0648\u0648 \u062e\u0628\u0631\u0629 \u0641\u064a \u0627\u0644\u062a\u0648\u0627\u0635\u0644 \u0627\u0644\u0645\u0647\u0646\u064a \u0648\u0628\u064a\u0626\u0627\u062a \u0627\u0644\u0639\u0645\u0644 \u0645\u062a\u0639\u062f\u062f\u0629 \u0627\u0644\u062b\u0642\u0627\u0641\u0627\u062a.',
          'Corporate learners are guided by instructors experienced in professional communication and multicultural workplace settings.'
        )}
        badgeLabel={pickText(lang, '\u062a\u062f\u0631\u064a\u0628 \u0645\u0624\u0633\u0633\u064a', 'Corporate Training')}
      />
      <section className="px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto grid max-w-7xl gap-8 xl:grid-cols-[0.44fr_0.56fr]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#6c8bc6]">{content.delivery.eyebrow}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">{content.delivery.title}</h2>
            <p className="mt-4 text-base leading-8 text-[#231f20]/72 dark:text-[#e6e6e6]/72">{content.delivery.description}</p>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            <Card className="rounded-[1.8rem] border border-[#231f20]/10 bg-white dark:border-white/10 dark:bg-[#231f20]">
              <CardContent className="p-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#231f20] text-white dark:bg-white/10"><Building2 className="h-5 w-5" /></div>
                <h3 className="mt-5 text-lg font-semibold">{content.delivery.locationsTitle}</h3>
                <div className="mt-4"><BulletList items={content.delivery.locations} /></div>
              </CardContent>
            </Card>
            <Card className="rounded-[1.8rem] border border-[#231f20]/10 bg-white dark:border-white/10 dark:bg-[#231f20]">
              <CardContent className="p-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#231f20] text-white dark:bg-white/10"><MonitorSmartphone className="h-5 w-5" /></div>
                <h3 className="mt-5 text-lg font-semibold">{content.delivery.schedulesTitle}</h3>
                <div className="mt-4"><BulletList items={content.delivery.schedules} /></div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-14 sm:px-6 lg:px-8 lg:py-20 dark:bg-[#231f20]">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#6c8bc6]">{content.process.eyebrow}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">{content.process.title}</h2>
          </div>
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {content.process.steps.map((item, index) => (
              <motion.div key={item.step} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-50px' }} transition={{ delay: index * 0.08, duration: 0.45 }}>
                <Card className="h-full rounded-[1.8rem] border border-[#231f20]/10 bg-[#e6e6e6] dark:border-white/10 dark:bg-white/5">
                  <CardContent className="p-6">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#231f20] text-lg font-bold text-white dark:bg-white/10">{item.step}</div>
                    <h3 className="mt-5 text-lg font-semibold">{item.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-[#231f20]/72 dark:text-[#e6e6e6]/72">{item.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto grid max-w-7xl gap-8 xl:grid-cols-[0.42fr_0.58fr]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#6c8bc6]">{content.reasons.eyebrow}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">{content.reasons.title}</h2>
          </div>
          <Card className="rounded-[2rem] border border-[#231f20]/10 bg-white dark:border-white/10 dark:bg-[#231f20]">
            <CardContent className="grid gap-4 p-6 sm:grid-cols-2 sm:p-8">
              {content.reasons.items.map((item) => (
                <div key={item} className="flex items-start gap-3 rounded-[1.3rem] bg-[#e6e6e6] px-4 py-4 dark:bg-white/5">
                  <Check className="mt-0.5 h-4 w-4 text-[#6c8bc6]" />
                  <span className="text-sm leading-7">{item}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="bg-[#231f20] px-4 py-14 text-white sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#e6e6e6]">{content.trusted.eyebrow}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">{content.trusted.title}</h2>
            <p className="mt-4 text-base leading-8 text-[#e6e6e6]/74">{content.trusted.description}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
            {content.trusted.logos.map((name) => (
              <div key={name} className="flex h-24 items-center justify-center rounded-[1.5rem] border border-white/10 bg-white/6 px-4 text-center text-sm font-semibold uppercase tracking-[0.12em] text-white/54 transition hover:border-white/20 hover:text-white/82">{name}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-7xl">
          <Card className="overflow-hidden rounded-[2.2rem] border border-[#231f20]/10 bg-white shadow-[0_28px_70px_-40px_rgba(15,23,42,0.3)] dark:border-white/10 dark:bg-[#231f20] dark:shadow-none">
            <CardContent className="grid gap-8 p-8 lg:grid-cols-[0.32fr_0.68fr] lg:p-10">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#6c8bc6]">{content.caseStudy.eyebrow}</p>
                <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em]">{content.caseStudy.title}</h2>
              </div>
              <div className="space-y-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  {content.caseStudy.items.map(({ label, value }) => (
                    <div key={label} className="rounded-[1.4rem] bg-[#e6e6e6] p-5 dark:bg-white/5">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#6c8bc6]">{label}</p>
                      <p className="mt-2 text-sm leading-7 text-[#231f20]/74 dark:text-[#e6e6e6]/74">{value}</p>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#6c8bc6]">{content.caseStudy.focusTitle}</p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2"><BulletList items={content.caseStudy.focusAreas} /></div>
                </div>
                <div className="rounded-[1.6rem] bg-[#231f20] px-6 py-5 text-white dark:bg-white/8">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#6c8bc6]">{content.caseStudy.resultTitle}</p>
                  <p className="mt-3 text-sm leading-7 text-white/82">{content.caseStudy.result}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section id="request-proposal" className="bg-white px-4 py-14 sm:px-6 lg:px-8 lg:py-20 dark:bg-[#231f20]">
        <div className="mx-auto max-w-7xl grid gap-8 xl:grid-cols-[0.4fr_0.6fr]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#6c8bc6]">{content.proposal.eyebrow}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">{content.proposal.title}</h2>
            <p className="mt-4 text-base leading-8 text-[#231f20]/72 dark:text-[#e6e6e6]/72">{content.proposal.description}</p>
          </div>
          <Card className="rounded-[2rem] border border-[#231f20]/10 bg-[#e6e6e6] dark:border-white/10 dark:bg-white/5">
            <CardContent className="p-6 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><label className="mb-1.5 block text-sm font-medium">{formCopy.companyName}</label><Input value={form.company_name} onChange={(e) => handleChange('company_name', e.target.value)} className="rounded-xl bg-white dark:bg-[#231f20]" /></div>
                  <div><label className="mb-1.5 block text-sm font-medium">{formCopy.contactPerson}</label><Input value={form.contact_person} onChange={(e) => handleChange('contact_person', e.target.value)} className="rounded-xl bg-white dark:bg-[#231f20]" /></div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><label className="mb-1.5 block text-sm font-medium">{formCopy.workEmail}</label><Input type="email" value={form.work_email} onChange={(e) => handleChange('work_email', e.target.value)} className="rounded-xl bg-white dark:bg-[#231f20]" /></div>
                  <div className="grid grid-cols-[120px_1fr] gap-3">
                    <div><label className="mb-1.5 block text-sm font-medium">{formCopy.code}</label><Select value={form.country_code} onValueChange={(value) => handleChange('country_code', value)}><SelectTrigger className="rounded-xl bg-white dark:bg-[#231f20]"><SelectValue /></SelectTrigger><SelectContent>{countryCodes.map((country) => <SelectItem key={country.code} value={country.code}>{country.code} {country.country}</SelectItem>)}</SelectContent></Select></div>
                    <div><label className="mb-1.5 block text-sm font-medium">{formCopy.phone}</label><Input value={form.phone} onChange={(e) => handleChange('phone', e.target.value)} className="rounded-xl bg-white dark:bg-[#231f20]" /></div>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><label className="mb-1.5 block text-sm font-medium">{formCopy.employeeCount}</label><Select value={form.employees_range} onValueChange={(value) => handleChange('employees_range', value)}><SelectTrigger className="rounded-xl bg-white dark:bg-[#231f20]"><SelectValue placeholder={formCopy.selectRange} /></SelectTrigger><SelectContent><SelectItem value="1-10">1-10</SelectItem><SelectItem value="11-50">11-50</SelectItem><SelectItem value="51-100">51-100</SelectItem><SelectItem value="101-250">101-250</SelectItem><SelectItem value="251-500">251-500</SelectItem><SelectItem value="500+">500+</SelectItem></SelectContent></Select></div>
                  <div><label className="mb-1.5 block text-sm font-medium">{formCopy.trainingLanguage}</label><Select value={form.training_language} onValueChange={(value) => handleChange('training_language', value)}><SelectTrigger className="rounded-xl bg-white dark:bg-[#231f20]"><SelectValue placeholder={formCopy.selectLanguage} /></SelectTrigger><SelectContent><SelectItem value="English">{formCopy.english}</SelectItem><SelectItem value="Arabic">{formCopy.arabic}</SelectItem></SelectContent></Select></div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><label className="mb-1.5 block text-sm font-medium">{formCopy.trainingType}</label><Select value={form.training_type} onValueChange={(value) => handleChange('training_type', value)}><SelectTrigger className="rounded-xl bg-white dark:bg-[#231f20]"><SelectValue placeholder={formCopy.selectType} /></SelectTrigger><SelectContent><SelectItem value="Group">{formCopy.group}</SelectItem><SelectItem value="Executive">{formCopy.executive}</SelectItem><SelectItem value="Intensive">{formCopy.intensive}</SelectItem></SelectContent></Select></div>
                  <div><label className="mb-1.5 block text-sm font-medium">{formCopy.location}</label><Select value={form.preferred_location} onValueChange={(value) => handleChange('preferred_location', value)}><SelectTrigger className="rounded-xl bg-white dark:bg-[#231f20]"><SelectValue placeholder={formCopy.selectLocation} /></SelectTrigger><SelectContent><SelectItem value="Company">{formCopy.company}</SelectItem><SelectItem value="Training Center">{formCopy.trainingCenter}</SelectItem><SelectItem value="Online">{formCopy.online}</SelectItem></SelectContent></Select></div>
                </div>
                <div><label className="mb-1.5 block text-sm font-medium">{formCopy.message}</label><Textarea value={form.message} onChange={(e) => handleChange('message', e.target.value)} className="min-h-[150px] rounded-xl bg-white dark:bg-[#231f20]" /></div>
                <Button type="submit" className="h-12 w-full rounded-full bg-[#6c8bc6] text-white hover:bg-[#6c8bc6]" disabled={submitting}>{submitting ? formCopy.sending : content.proposal.submitLabel} <Send className="ml-2 h-4 w-4" /></Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="px-4 pb-16 sm:px-6 lg:px-8 lg:pb-24">
        <div className="mx-auto max-w-7xl">
          <Card className="bf-corporate-final-cta overflow-hidden rounded-[2.5rem] border border-[#e6e6e6] bg-[#6c8bc6] text-white shadow-[0_32px_80px_-40px_rgba(15,23,42,0.45)]">
            <CardContent className="grid gap-8 p-8 sm:p-10 xl:grid-cols-[1fr_auto] xl:items-center">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-white/80">{content.finalCta.eyebrow}</p>
                <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">{content.finalCta.title}</h2>
                <p className="mt-4 max-w-2xl text-base leading-8 text-white/85">{content.finalCta.description}</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row xl:flex-col">
                <Button type="button" className="h-12 rounded-full bg-white px-7 text-sm font-semibold text-[#231f20] hover:bg-white/90" onClick={scrollToProposal}>{content.finalCta.primaryLabel} <ArrowRight className="ml-2 h-4 w-4" /></Button>
                <Button asChild variant="outline" className="h-12 rounded-full border-white/40 bg-transparent px-7 text-sm font-semibold text-white hover:bg-white/10"><Link to="/contact?type=corporate">{content.finalCta.secondaryLabel}</Link></Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
